/**
 * The three ways into one account, and how to gain the ones you did not sign
 * up with.
 *
 * Signing up with GitHub or Google leaves you with no password; signing up
 * with a password leaves you with neither. Any one is fine until the day
 * another is what you have — a machine without your GitHub session, a Google
 * outage — so this pane exists to make each account hold as many as its
 * owner wants.
 *
 * The rule underneath every control here: nobody leaves with no way back in.
 * Unlinking a provider is refused while it is the only way in — meaning
 * neither a password nor the *other* provider is set — and the server refuses
 * it too rather than trusting this pane to have hidden the button.
 */
import { useState } from 'react'

import { api } from '../api/client'
import type { Me } from '../api/types'
import { radius } from '../components/primitives/shape'

/** Where the browser goes to hand itself to GitHub and come back linked. */
const GITHUB_LINK = '/v1/auth/github/link'
/** Where the browser goes to hand itself to Google and come back linked. */
const GOOGLE_LINK = '/v1/auth/google/link'

const field = {
  fontSize: 13.5, color: 'var(--text)', background: 'var(--panel-strong)',
  border: '1px solid var(--border-strong)', borderRadius: radius.sm,
  padding: '9px 12px',
} as const

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, padding: '14px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 4 }}>{label}</div>
        {children}
      </div>
    </div>
  )
}

function Quiet({ children }: { children: React.ReactNode }) {
  return (
    <p style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-dim)', margin: 0 }}>
      {children}
    </p>
  )
}

function Action({ children, onClick, tone = 'plain', disabled }: {
  children: React.ReactNode
  onClick: () => void
  tone?: 'plain' | 'danger'
  disabled?: boolean
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 14px', borderRadius: radius.sm, fontSize: 12.5, fontWeight: 700,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.45 : 1,
        background: tone === 'danger' ? 'rgba(229,98,74,.12)' : 'var(--panel-strong)',
        color: tone === 'danger' ? 'var(--deletions)' : 'var(--text-dim)',
        border: tone === 'danger' ? 'none' : '1px solid var(--border-strong)',
      }}
    >
      {children}
    </button>
  )
}

export function WaysIn({ me, githubConfigured, googleConfigured, onMeChanged }: {
  me: Me
  /** False when this server has no GitHub App at all — then there is no link
   *  to offer, and a button that 503s is worse than no button. */
  githubConfigured: boolean
  /** Same idea, for Google. */
  googleConfigured: boolean
  onMeChanged?: (me: Me) => void
}) {
  const [changing, setChanging] = useState(false)
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  /**
   * What just happened, not merely that something did. Both leave
   * `has_password` true, and telling someone who has just set their first
   * password that every other browser was signed out would be describing a
   * consequence that only belongs to the other case.
   */
  const [saved, setSaved] = useState<'set' | 'changed' | null>(null)

  async function save() {
    const changing = me.has_password
    setBusy(true)
    setFailure(null)
    try {
      const updated = await api.setPassword({
        ...(changing ? { current_password: current } : {}),
        new_password: next,
      })
      onMeChanged?.(updated)
      setChanging(false)
      setCurrent('')
      setNext('')
      setSaved(changing ? 'changed' : 'set')
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'not saved')
    } finally {
      setBusy(false)
    }
  }

  async function unlinkGithub() {
    setFailure(null)
    try {
      onMeChanged?.(await api.unlinkGithub())
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'not saved')
    }
  }

  async function unlinkGoogle() {
    setFailure(null)
    try {
      onMeChanged?.(await api.unlinkGoogle())
    } catch (error) {
      setFailure(error instanceof Error ? error.message : 'not saved')
    }
  }

  /** Whichever provider is not the one being asked about — the other way in
   *  that makes it safe to unlink this one. */
  const hasOtherWayIn = {
    github: me.has_password || Boolean(me.google_email),
    google: me.has_password || Boolean(me.github_login),
  }

  return (
    <>
      <Row label="Email address">
        <Quiet>{me.email}</Quiet>
      </Row>

      <Row label="GitHub">
        {me.github_login ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Quiet>
              Linked to{' '}
              <strong style={{ color: 'var(--text)', fontWeight: 700 }}>
                @{me.github_login}
              </strong>
            </Quiet>
            <div style={{ flex: 1 }} />
            {/* Shown but refused rather than hidden: "why can I not unlink"
                is a better question to be able to answer than a missing
                button, and the server refuses it either way. */}
            <Action tone="danger" onClick={unlinkGithub} disabled={!hasOtherWayIn.github}>
              Unlink
            </Action>
          </div>
        ) : githubConfigured ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Quiet>Not linked. Linking lets you sign in with GitHub as well.</Quiet>
            <div style={{ flex: 1 }} />
            <a
              href={GITHUB_LINK}
              style={{
                padding: '8px 14px', borderRadius: radius.sm, background: 'var(--text)',
                color: 'var(--ground)', fontSize: 12.5, fontWeight: 700,
              }}
            >
              Link GitHub
            </a>
          </div>
        ) : (
          <Quiet>This server has no GitHub App configured.</Quiet>
        )}
        {me.github_login && !hasOtherWayIn.github && (
          <p style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-faint)', margin: '8px 0 0' }}>
            Set a password first, or link a Google account. GitHub is the only
            way into this account, and unlinking it would leave you outside.
          </p>
        )}
      </Row>

      <Row label="Google">
        {me.google_email ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Quiet>
              Linked to{' '}
              <strong style={{ color: 'var(--text)', fontWeight: 700 }}>
                {me.google_email}
              </strong>
            </Quiet>
            <div style={{ flex: 1 }} />
            <Action tone="danger" onClick={unlinkGoogle} disabled={!hasOtherWayIn.google}>
              Unlink
            </Action>
          </div>
        ) : googleConfigured ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Quiet>Not linked. Linking lets you sign in with Google as well.</Quiet>
            <div style={{ flex: 1 }} />
            <a
              href={GOOGLE_LINK}
              style={{
                padding: '8px 14px', borderRadius: radius.sm, background: 'var(--text)',
                color: 'var(--ground)', fontSize: 12.5, fontWeight: 700,
              }}
            >
              Link Google
            </a>
          </div>
        ) : (
          <Quiet>This server has no Google client configured.</Quiet>
        )}
        {me.google_email && !hasOtherWayIn.google && (
          <p style={{ fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-faint)', margin: '8px 0 0' }}>
            Set a password first, or link a GitHub account. Google is the only
            way into this account, and unlinking it would leave you outside.
          </p>
        )}
      </Row>

      <Row label="Password">
        {!changing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Quiet>
              {saved === 'changed' ? 'Changed. Every other browser was signed out.'
                : saved === 'set' ? 'Set. You can sign in with it now.'
                  : me.has_password ? 'Set.'
                    : `Not set — you sign in with ${me.github_login ? 'GitHub' : 'Google'}.`}
            </Quiet>
            <div style={{ flex: 1 }} />
            <Action onClick={() => { setChanging(true); setSaved(null); setFailure(null) }}>
              {me.has_password ? 'Change password' : 'Set a password'}
            </Action>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 320 }}>
            {me.has_password && (
              <input
                type="password"
                aria-label="Current password"
                placeholder="Current password"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
                style={field}
              />
            )}
            <input
              type="password"
              aria-label="New password"
              placeholder="New password"
              autoComplete="new-password"
              value={next}
              onChange={(event) => setNext(event.target.value)}
              style={field}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <Action onClick={() => void save()} disabled={busy || !next}>Save</Action>
              <Action onClick={() => { setChanging(false); setFailure(null) }}>Cancel</Action>
            </div>
            {/* Said before the attempt, not after it: the server refuses a
                short one, and being told the rule afterwards is a wasted trip. */}
            <p style={{ fontSize: 11.5, color: 'var(--text-faint)', margin: 0 }}>
              At least 10 characters.
              {me.has_password && ' Changing it signs out every other browser.'}
            </p>
          </div>
        )}
      </Row>

      {failure && (
        <div role="alert" style={{ fontSize: 11.5, color: 'var(--deletions)', marginTop: 4 }}>
          {failure}
        </div>
      )}
    </>
  )
}
